import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import Button, { type ButtonProps } from "../Button/Button";
import Icon from "../Icon/Icon";
import Tooltip, { type TooltipContentProps } from "../Tooltip/Tooltip";
import styles from "./SidebarNav.module.scss";

export type SidebarNavTheme = "light" | "dark";
export type SidebarNavCollapsible = "offcanvas" | "icon" | "none";

export interface SidebarNavContextValue {
  collapsed: boolean;
}

const SidebarNavContext = React.createContext<SidebarNavContextValue | null>(null);

export function useSidebarNav() {
  return React.useContext(SidebarNavContext);
}

type DivProps = React.ComponentPropsWithoutRef<"div">;
type AsideProps = React.ComponentPropsWithoutRef<"aside">;
type MainProps = React.ComponentPropsWithoutRef<"main">;
type UlProps = React.ComponentPropsWithoutRef<"ul">;
type LiProps = React.ComponentPropsWithoutRef<"li">;
type ButtonElementProps = React.ComponentPropsWithoutRef<"button">;
type AnchorProps = React.ComponentPropsWithoutRef<"a">;

export interface SidebarNavProps extends Omit<DivProps, "children"> {
  children: React.ReactNode;
  collapsed?: boolean;
  theme?: SidebarNavTheme;
  collapsible?: SidebarNavCollapsible;
  width?: number | string;
  iconWidth?: number | string;
}

const cssSize = (size?: number | string) => (typeof size === "number" ? `${size}px` : size);

const DefaultSidebarNav = ({
  children,
  collapsed = false,
  theme = "light",
  collapsible = "icon",
  width,
  iconWidth,
  className,
  style,
  ...props
}: SidebarNavProps) => {
  const rootStyle = {
    ...style,
    "--sidebar-width": cssSize(width),
    "--sidebar-width-icon": cssSize(iconWidth),
  } as React.CSSProperties;
  const contextValue = React.useMemo<SidebarNavContextValue>(
    () => ({
      collapsed,
    }),
    [collapsed],
  );

  return (
    <SidebarNavContext.Provider value={contextValue}>
      <div
        data-deci-sidebar-nav
        data-theme={theme}
        data-state={collapsed ? "collapsed" : "expanded"}
        data-collapsed={collapsed ? "true" : "false"}
        data-collapsible={collapsed ? collapsible : ""}
        className={clsx(styles.root, className)}
        style={rootStyle}
        {...props}
      >
        {children}
      </div>
    </SidebarNavContext.Provider>
  );
};

const SidebarNav = createPluggable<SidebarNavProps>("deci.sidebar-nav", DefaultSidebarNav);

export interface SidebarNavPanelProps extends AsideProps {
  asChild?: boolean;
}

const DefaultPanel = ({ asChild = false, className, children, ...props }: SidebarNavPanelProps) => {
  const Component = (asChild ? Slot : "aside") as React.ElementType;
  return (
    <Component
      data-slot="sidebar"
      className={clsx(styles.sidebar, className)}
      {...props}
    >
      <div
        data-slot="sidebar-inner"
        className={styles.inner}
      >
        {children}
      </div>
    </Component>
  );
};

const Panel = createPluggable<SidebarNavPanelProps>("deci.sidebar-nav.panel", DefaultPanel);

const Header = ({ className, ...props }: DivProps) => (
  <div
    data-slot="sidebar-header"
    className={clsx(styles.header, className)}
    {...props}
  />
);

const Footer = ({ className, ...props }: DivProps) => (
  <div
    data-slot="sidebar-footer"
    className={clsx(styles.footer, className)}
    {...props}
  />
);

const Content = ({ className, ...props }: DivProps) => (
  <div
    data-slot="sidebar-content"
    className={clsx(styles.content, className)}
    {...props}
  />
);

const Main = ({ className, ...props }: MainProps) => (
  <main
    data-slot="sidebar-main"
    className={clsx(styles.main, className)}
    {...props}
  />
);

const Group = ({ className, ...props }: DivProps) => (
  <div
    data-slot="sidebar-group"
    className={clsx(styles.group, className)}
    {...props}
  />
);

const GroupLabel = ({ className, asChild = false, ...props }: DivProps & { asChild?: boolean }) => {
  const Component = (asChild ? Slot : "div") as React.ElementType;
  return (
    <Component
      data-slot="sidebar-group-label"
      className={clsx(styles.groupLabel, className)}
      {...props}
    />
  );
};

const GroupContent = ({ className, ...props }: DivProps) => (
  <div
    data-slot="sidebar-group-content"
    className={clsx(styles.groupContent, className)}
    {...props}
  />
);

const Menu = ({ className, ...props }: UlProps) => (
  <ul
    data-slot="sidebar-menu"
    className={clsx(styles.menu, className)}
    {...props}
  />
);

const MenuItem = ({ className, ...props }: LiProps) => (
  <li
    data-slot="sidebar-menu-item"
    className={clsx(styles.menuItem, className)}
    {...props}
  />
);

export interface MenuButtonProps extends ButtonElementProps {
  asChild?: boolean;
  isActive?: boolean;
  collapsed?: boolean;
  tooltip?: string | TooltipContentProps;
  ref?: React.Ref<HTMLButtonElement>;
}

const MenuButton = withRefProp(
  ({ asChild = false, isActive = false, collapsed = false, tooltip, className, ref, ...props }: MenuButtonProps) => {
    const Component = (asChild ? Slot : "button") as React.ElementType;
    const sidebarNavContext = useSidebarNav();
    const tooltipVisible = collapsed || Boolean(sidebarNavContext?.collapsed);

    const button = (
      <Component
        ref={ref}
        data-slot="sidebar-menu-button"
        data-active={isActive}
        className={clsx(styles.menuButton, className)}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    const tooltipProps = typeof tooltip === "string" ? { children: tooltip } : tooltip;

    return (
      <Tooltip>
        <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
        <Tooltip.Content
          placement="right"
          hidden={!tooltipVisible}
          {...tooltipProps}
        />
      </Tooltip>
    );
  },
);

const MenuSub = ({ className, ...props }: UlProps) => (
  <ul
    data-slot="sidebar-menu-sub"
    className={clsx(styles.menuSub, className)}
    {...props}
  />
);

const MenuSubItem = ({ className, ...props }: LiProps) => (
  <li
    data-slot="sidebar-menu-sub-item"
    className={clsx(styles.menuSubItem, className)}
    {...props}
  />
);

const MenuSubButton = ({
  asChild = false,
  isActive = false,
  className,
  ...props
}: AnchorProps & { asChild?: boolean; isActive?: boolean }) => {
  const Component = (asChild ? Slot : "a") as React.ElementType;
  return (
    <Component
      data-slot="sidebar-menu-sub-button"
      data-active={isActive}
      className={clsx(styles.menuSubButton, className)}
      {...props}
    />
  );
};

const Trigger = ({ className, label, ...props }: ButtonProps) => {
  return (
    <Button
      data-slot="sidebar-trigger"
      variant="ghost"
      size="sm"
      className={clsx(styles.trigger, className)}
      {...props}
      label={label}
    >
      {props.children ?? <Icon name="menu" />}
    </Button>
  );
};

export default Object.assign(SidebarNav, {
  Panel,
  Header,
  Footer,
  Content,
  Main,
  Group,
  GroupLabel,
  GroupContent,
  Menu,
  MenuItem,
  MenuButton,
  MenuSub,
  MenuSubItem,
  MenuSubButton,
  Trigger,
  useSidebarNav,
});
